import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { Button, Input, InputCalendar, InputDropdown, InputOptionBox, InputType } from '../../../../core/components/Index';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import Helper, { formatDate } from '../../../../core/services/Helper';
import { BeneficiaryRelation } from '../OraEnums';
import { Plus } from 'lucide-react';
import CountryList from '../../../../assets/data/Country.json';
import { useEffect, useRef, useState } from 'react';

const BeneficiaryDesignationBurialAllowance = observer(() => {

  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  const errorRef = useRef<HTMLDivElement>(null);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.BeneficiaryBurialAllowance, memberId!);
  }, []);

  useEffect(() => {
    if (vm.beneficiaryBurialCurrentState?.validationMessage !== "" && errorRef.current) {
      errorRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [vm.beneficiaryBurialCurrentState?.validationMessage, submitAttempt]);

  const handleSubmit = async (submitFunction: () => Promise<boolean>) => {
    setSubmitAttempt(prev => prev + 1);
    await submitFunction();
  };

  return (
    <>
      {
        vm.beneficiaryBurialCurrentState!.validationMessage !== "" &&
        <>
          <div ref={errorRef} className="gap-4 p-6 border-[2px] border-[#D82042] bg-[#d8204212] flex rounded-md mb-4" >
            <div><svg viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-7 h-7'>
              <path d="M12.5 25.5C15.8152 25.5 18.9946 24.183 21.3388 21.8388C23.683 19.4946 25 16.3152 25 13C25 9.68479 23.683 6.50537 21.3388 4.16117C18.9946 1.81696 15.8152 0.5 12.5 0.5C9.18479 0.5 6.00537 1.81696 3.66117 4.16117C1.31696 6.50537 0 9.68479 0 13C0 16.3152 1.31696 19.4946 3.66117 21.8388C6.00537 24.183 9.18479 25.5 12.5 25.5ZM12.5 6.75C13.1494 6.75 13.6719 7.27246 13.6719 7.92188V13.3906C13.6719 14.04 13.1494 14.5625 12.5 14.5625C11.8506 14.5625 11.3281 14.04 11.3281 13.3906V7.92188C11.3281 7.27246 11.8506 6.75 12.5 6.75ZM10.9375 17.6875C10.9375 17.2731 11.1021 16.8757 11.3951 16.5826C11.6882 16.2896 12.0856 16.125 12.5 16.125C12.9144 16.125 13.3118 16.2896 13.6049 16.5826C13.8979 16.8757 14.0625 17.2731 14.0625 17.6875C14.0625 18.1019 13.8979 18.4993 13.6049 18.7924C13.3118 19.0854 12.9144 19.25 12.5 19.25C12.0856 19.25 11.6882 19.0854 11.3951 18.7924C11.1021 18.4993 10.9375 18.1019 10.9375 17.6875Z" fill="#D82042" />
            </svg>
            </div>
            <div className='-mt-1'>
              <div className='text-[#D82042] text-22px font-semibold mb-1 tracking--1percent'>{vm.beneficiaryBurialCurrentState?.validationMessageTitle}</div>
              <p className="text-black text-15px tracking--1_5percent  ">
                {Helper.formattedHtml(vm.beneficiaryBurialCurrentState!.validationMessage)}
              </p>
            </div>
          </div>
        </>
      }
      <div className="space-y-6 text-gray-900">
        <h3 className="text-2xl md:text-3xl font-semibold mb-4">Beneficiary Designation</h3>
        {vm.model.beneficiaryDesignation.burialBeneficiaries?.length === 0 &&
          <>
            <p>Great! Now let's move on to your burial allowance beneficiary.</p>
            <h4 className="text-xl font-semibold">Burial Allowance</h4>
            <p>
              As a SCERS member, you may designate a beneficiary to receive the burial allowance provided by SCERS in the event of your passing. This allowance—up to $4,000—is intended to help cover funeral and burial expenses.
            </p>
            <p>
              To ensure the allowance is distributed according to your wishes, please provide accurate and up-to-date information. If you need to change your designation in the future, you can do so by submitting a new designation form.
            </p>
            <p>
              You may choose to use the same beneficiary(ies) you named for your retirement benefit, or you can name different individuals or entities for this purpose.
            </p>
            <p className="font-semibold">Please make a selection:</p>
            <div className="flex items-start space-x-2">
              <InputOptionBox
                inputId="wishToUseSameBeneficiaries"
                value={'same'}
                checked={vm.beneficiaryBurialCurrentState?.wishOption === 'same'}
                onChange={(e: any) => vm.onInputChange(e, 'wishOption', vm.beneficiaryBurialCurrentState)}
              />
              <label htmlFor="wishToUseSameBeneficiaries" className="ml-2 text-base font-medium">
                I wish to use the same beneficiary(ies) I listed for my continuance/lump-sum benefits (listed below).
              </label>
            </div>
            <div>
              {vm.model.beneficiaryDesignation?.beneficiaries?.map((beneficiary, index) => (
                <div className="ml-12" key={`bene${index}`}>
                  {vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key !== BeneficiaryRelation.EntityTrustOrg &&
                    // vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key !== BeneficiaryRelation.NoEligibleBeneficiary &&
                    <p>Beneficiary {index + 1}: {beneficiary.beneFirstName} {beneficiary.beneLastName}</p>
                  }
                  {vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key === BeneficiaryRelation.EntityTrustOrg &&
                    <p>Beneficiary {index + 1}: {beneficiary.beneOtherName}</p>
                  }
                  <p className={vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key === BeneficiaryRelation.EntityTrustOrg ? 'mb-4' : ''}>
                    Relationship: {vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.value}</p>
                  {vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key !== BeneficiaryRelation.EntityTrustOrg &&
                    // vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key !== BeneficiaryRelation.NoEligibleBeneficiary &&
                    <p className='mb-4'>Date of Birth: {formatDate(beneficiary.beneBirthDate)}</p>}
                  {/* {vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key === BeneficiaryRelation.EntityTrustOrg &&
                    vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.key === BeneficiaryRelation.NoEligibleBeneficiary &&
                    <p className='mb-4'></p>
                  } */}
                </div>
              ))}
            </div>
            <div className="flex items-start space-x-2">
              <InputOptionBox
                inputId="wishToUseDifferentBeneficiaries"
                value={'different'}
                checked={vm.beneficiaryBurialCurrentState?.wishOption === 'different'}
                onChange={(e: any) => vm.onInputChange(e, 'wishOption', vm.beneficiaryBurialCurrentState)}
              />
              <label htmlFor="wishToUseDifferentBeneficiaries" className="ml-2 text-base font-medium">
                I wish to change or designate a new beneficiary(ies) specifically for the burial allowance.
              </label>
            </div>
            <div className="flex space-x-4 mt-6">
              <Button
                title="Save & Continue" rounded
                onClick={() => vm.cloneBeneficiaryBenefitToBurialAllowance()}
                disabled={!(vm.beneficiaryBurialCurrentState?.wishOption)}
              />
            </div>
          </>
        }
        {
          vm.model.beneficiaryDesignation.burialBeneficiaries?.length! > 0 &&
          <>
            <h4 className="text-xl font-semibold">Burial Allowance</h4>

            {!vm.beneficiaryBurialCurrentState!.isAddingBeneficiary && !vm.beneficiaryBurialCurrentState!.isEditingBeneficiary
              && <>
                {vm.beneficiaryBurialCurrentState?.addNewTopButtonVisible &&
                  <button onClick={async () => await vm.addBurialBeneficiaryAsync(vm.burialBeneficiaryModel.lovBeneRelationshipId!)}
                    className="hover:bg-primary-50/20 cursor-pointer w-full flex flex-col items-center justify-center  rounded-lg p-5 text-center"
                    style={{
                      backgroundImage: "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%23D0CDC8' stroke-width='6' stroke-dasharray='0%2c 12' stroke-dashoffset='12' stroke-linecap='square'/%3e%3c/svg%3e\")",
                      borderRadius: "6px"
                    }}>
                    <p className="text-primary-500 text-22px font-semibold">Add Another Beneficiary</p>
                    <div className="mt-2 bg-primary-500 text-white rounded-full w-10 h-10 flex items-center justify-center border border-primary-600">
                      <Plus className='w-8 h-8' />
                    </div>
                  </button>
                }
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vm.model.beneficiaryDesignation?.burialBeneficiaries?.map((beneficiary, index) => (
                    <div key={index} className="p-6 bg-cream-50 rounded-lg flex flex-col justify-between">
                      <h4 className="text-22px  mb-4 text-primary-500 font-semibold">{vm.model.beneficiaryDesignation?.burialBeneficiaries?.length === 1 ? 'Beneficiary' : `Beneficiary ${index + 1}`}</h4>
                      {/* {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.NoEligibleBeneficiary && */}
                      <div className='flex flex-col flex-1'>
                        {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (
                          <div className="text-gray-700 py-1.5 flex items-start justify-between">
                            <dt className="text-15px font-normal leading-6 ">Name</dt>
                            <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">
                              {beneficiary.beneFirstName} {beneficiary.beneLastName}
                            </dd>
                          </div>)}
                        {beneficiary.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg && (
                          <div className="text-gray-700 py-1.5 flex items-start justify-between">
                            <dt className="text-15px font-normal leading-6 ">Entity Name</dt>
                            <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">
                              {beneficiary.beneOtherName}
                            </dd>
                          </div>)}
                        <div className="text-gray-700 py-1.5 flex items-start justify-between">
                          <dt className="text-15px font-normal leading-6 ">Relationship</dt>
                          <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.value} </dd>
                        </div>
                        {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (
                          <div className="text-gray-700 py-1.5 flex items-start justify-between">
                            <dt className="text-15px font-normal leading-6 ">Date of Birth</dt>
                            <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{formatDate(beneficiary.beneBirthDate)} </dd>
                          </div>
                        )}
                        {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg &&
                          <div className="text-gray-700 py-1.5 flex items-start justify-between">
                            <dt className="text-15px font-normal leading-6 ">SSN</dt>
                            <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">XXX-XX-{beneficiary.beneSsn?.substring(5, 9)}</dd>
                          </div>
                        }
                        {beneficiary.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg &&
                          <>
                            <div className="text-gray-700 py-1.5 flex items-start justify-between">
                              &nbsp;
                            </div>
                            <div className="text-gray-700 py-1.5 flex items-start justify-between">
                              <dt className="text-15px font-normal leading-6 ">TIN</dt>
                              <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{beneficiary.beneTin!}</dd>
                            </div>
                          </>
                        }
                        <div className="text-gray-700 py-1.5 flex items-start justify-between">
                          <dt className="text-15px font-normal leading-6">Address</dt>
                          <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">
                            {Helper.getFormattedAddress(beneficiary.beneStreetAddress, beneficiary.beneStreetAddress2, beneficiary.beneCity, beneficiary.beneState, beneficiary.beneZip, beneficiary.beneCountry)}
                          </dd>
                        </div>
                        <div className='flex-1' />
                        <div className="text-gray-700 py-1.5 flex items-start justify-between border-t mt-2 items-center">
                          <dt className="text-15px font-normal leading-6 sm:mt-2 font-semibold">Percentage of Allowance</dt>
                          <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-2 text-right font-semibold">{beneficiary.burialPercentage}%</dd>
                        </div>
                      </div>
                      {/* } */}
                      {/* {beneficiary?.lovBeneRelationshipId === BeneficiaryRelation.NoEligibleBeneficiary &&
                        <div className="text-gray-700 py-1.5 flex items-start justify-between">
                          <dt className="text-15px font-normal leading-6 ">Relationship</dt>
                          <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.value} </dd>
                        </div>
                      } */}
                      {!vm.beneficiaryBurialCurrentState!.summaryVisible && <div className="pt-6 flex items-center justify-center border-t mt-2">
                        <Button className="mr-2" title="Edit" rounded onClick={async () => await vm.editBurialBeneficiaryAsync(index)} />
                        <Button title="Delete" severity='warning' outlined rounded style={{ backgroundColor: 'white', color: 'rgb(207 113 25)' }} onClick={() => Appl.ConfirmBox.show('If you proceed, this beneficiary will be permanently removed from your retirement application, and you will need to re-enter all their information if you wish to add them again later.', 'Are You Sure You Wish to Delete?', '', "Yes, I am sure", 'Cancel', async () => await vm.removeBurialBeneficiaryAsync(index), undefined, true)} />
                      </div>
                      }
                    </div>
                  ))}
                  {vm.beneficiaryBurialCurrentState?.addNewSideButtonVisible &&
                    <button onClick={async () => await vm.addBurialBeneficiaryAsync(vm.burialBeneficiaryModel.lovBeneRelationshipId!)}
                      className="hover:bg-primary-50/20 cursor-pointer w-full flex flex-col items-center justify-center  rounded-lg p-5 text-center"
                      style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%23D0CDC8' stroke-width='6' stroke-dasharray='0%2c 12' stroke-dashoffset='12' stroke-linecap='square'/%3e%3c/svg%3e\")",
                        borderRadius: "6px"
                      }}>
                      <p className="text-primary-500 text-22px font-semibold">{vm.burialBeneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild ? 'Add Another Minor/Dependent Beneficiary' : 'Add Another Beneficiary'}</p>
                      <div className="mt-2 bg-primary-500 text-white rounded-full w-10 h-10 flex items-center justify-center border border-primary-600">
                        <Plus className='w-8 h-8' />
                      </div>
                    </button>
                  }
                </div>
              </>
            }

          </>
        }

        {
          vm.beneficiaryBurialCurrentState!.isAddingBeneficiary || vm.beneficiaryBurialCurrentState!.isEditingBeneficiary ? (
            <div className="space-y-6 max-w-lg">
               <div className="flex sm:space-x-4 space-y-4 sm:space-y-0 flex-col sm:flex-row">
                <div className="md:w-1/2 w-full">
                  <InputDropdown
                    id="lovBeneRelationshipId"
                    value={vm.burialBeneficiaryModel?.lovBeneRelationshipId}
                    options={vm.beneficiaryBurialCurrentState?.relationships}
                    optionValue="key"
                    optionLabel="value"
                    label="Relationship"
                    placeholder="Select"
                    onChange={(e) => {
                      vm.onInputChange(e, 'lovBeneRelationshipId', vm.burialBeneficiaryModel);
                    }}
                    className="w-full mt-1 flex-1 md:!w-full"
                    required={false}
                  />
                </div>

                <div className="md:w-1/2 min-w-[60px]">
                  <div className="flex items-center">
                    <div className="h-full">
                      <Input
                        id="burialPercentage"
                        value={vm.burialBeneficiaryModel?.burialPercentage}
                        label="Benefit Percentage"
                        inputType={InputType.Number}
                        onChange={(e) => vm.onInputChange(e, 'burialPercentage', vm.burialBeneficiaryModel)}
                        placeholder="Benefit Percentage"
                        maxLength={5}
                        required={false}
                        prefix="%"
                        prefixPosition="right" />

                    </div>
                  </div>
                </div>

              </div>
              {vm.burialBeneficiaryModel?.lovBeneRelationshipId &&
                // vm.burialBeneficiaryModel?.lovBeneRelationshipId !== BeneficiaryRelation.NoEligibleBeneficiary && 
                <>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6 '>
                    {vm.burialBeneficiaryModel?.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (<>
                      <div>
                        <Input
                          id="beneFirstName"
                          value={vm.burialBeneficiaryModel?.beneFirstName}
                          inputType={InputType.Text}
                          label="First Name"
                          onChange={(e) => vm.onInputChange(e, 'beneFirstName', vm.burialBeneficiaryModel)}
                          placeholder="First Name"
                          required={false}
                        />
                      </div>
                      <div>
                        <Input
                          id="beneLastName"
                          value={vm.burialBeneficiaryModel?.beneLastName}
                          inputType={InputType.Text}
                          label="Last Name"
                          onChange={(e) => vm.onInputChange(e, 'beneLastName', vm.burialBeneficiaryModel)}
                          placeholder="Last Name"
                          required={false}
                        />
                      </div>
                      <div>
                        <InputCalendar
                          id="beneBirthDate"
                          value={vm.burialBeneficiaryModel?.beneBirthDate!}
                          label="Date of Birth"
                          showHint={true}
                          onChange={(e) => {
                            vm.onInputChange(e, 'beneBirthDate', vm.burialBeneficiaryModel);
                          }}
                          required={false}
                          maxDate={new Date()}
                        />
                      </div>
                      <div>
                        {vm.burialBeneficiaryModel?.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (
                          <label className="font-semibold mb-2 block" htmlFor="beneSsn">
                            Social Security Number <span className="block font-light block text-[13px] font-normal text-gray-900">9 digits, no dashes or other characters</span>
                          </label>
                        )}
                        <Input
                          id="beneSsn"
                          value={vm.burialBeneficiaryModel?.beneSsn}
                          inputType={InputType.Secret}
                          onChange={(e) => vm.onInputChange(e, 'beneSsn', vm.burialBeneficiaryModel)}
                          placeholder=""
                          maxLength={9}
                          style={{ width: '800px' }}
                          required={false}
                        />
                      </div>
                    </>
                    )}
                    {vm.burialBeneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg && (<>
                      <div className='col-span-2'>
                        <Input
                          id="beneOtherName"
                          value={vm.burialBeneficiaryModel?.beneOtherName}
                          inputType={InputType.Text}
                          label="Entity Name"
                          onChange={(e) => vm.onInputChange(e, 'beneOtherName', vm.burialBeneficiaryModel)}
                          style={{ width: '500px' }}
                          required={false}
                        />
                      </div>

                      <div>
                        <label className="font-semibold mb-2 block" htmlFor="beneTin">
                          Tax Identification Number <span className="block font-light block text-[13px] font-normal text-gray-900">9 digits, no dashes or other characters</span>
                        </label>
                        <Input
                          id="beneTin"
                          value={vm.burialBeneficiaryModel?.beneTin}
                          inputType={InputType.Secret}
                          onChange={(e) => vm.onInputChange(e, 'beneTin', vm.burialBeneficiaryModel)}
                          placeholder=""
                          maxLength={9}
                          style={{ width: '500px' }}
                          required={false}
                        />
                      </div>
                      <div></div>
                    </>)}

                    <div className='md:col-span-2'>
                      <h4 className="text-gray-700 font-medium text-22px mb-4">Beneficiary Address</h4>
                      <div>
                        <InputDropdown
                          id="beneCountry"
                          value={vm.burialBeneficiaryModel?.beneCountry}
                          options={CountryList}
                          optionValue="key"
                          optionLabel="key"
                          label="Country"
                          placeholder='Select Country'
                          onChange={(e) => vm.onInputChange(e, 'beneCountry', vm.burialBeneficiaryModel)}
                          className='!w-full md:max-w-[600px]'
                          required={false}
                        />
                      </div>
                      <div className="mt-2 mb-4">
                        <Input
                          id="beneStreetAddress"
                          value={vm.burialBeneficiaryModel?.beneStreetAddress}
                          inputType={InputType.Text}
                          label="Address Line 1"
                          onChange={(e) => {
                            vm.onInputChange(e, 'beneStreetAddress', vm.burialBeneficiaryModel);
                          }}
                          required={false}
                         className='md:max-w-[600px]'
                        />
                      </div>
                      <div className="mt-2 mb-4">
                        <Input
                          id="beneStreetAddress2"
                          value={vm.burialBeneficiaryModel?.beneStreetAddress2}
                          inputType={InputType.Text}
                          label="Address Line 2"
                          onChange={(e) => {
                            vm.onInputChange(e, 'beneStreetAddress2', vm.burialBeneficiaryModel);
                          }}
                          required={false}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3  gap-4 ">
                        <div>
                          <Input
                            id="beneCity"
                            value={vm.burialBeneficiaryModel?.beneCity}
                            inputType={InputType.Text}
                            onChange={(e) => {
                              vm.onInputChange(e, 'beneCity', vm.burialBeneficiaryModel);
                            }}
                            label="City"
                            className='md:max-w-[600px]'
                            required={false}

                          />
                        </div>
                        <div>
                          <Input
                            id="beneState"
                            value={vm.burialBeneficiaryModel?.beneState}
                            inputType={InputType.Text}
                            onChange={(e) => {
                              vm.onInputChange(e, 'beneState', vm.burialBeneficiaryModel);
                            }}
                            label={vm.burialBeneficiaryModel?.beneCountry === 'United States' ? 'State' : 'State/Province/Region'}
                            required={false}
                          />
                        </div>
                        <div>
                          <Input
                            id="beneZip"
                            value={vm.burialBeneficiaryModel?.beneZip}
                            inputType={InputType.Text}
                            onChange={(e) => {
                              vm.onInputChange(e, 'beneZip', vm.burialBeneficiaryModel);
                            }}
                            label={vm.burialBeneficiaryModel?.beneCountry === 'United States' ? 'Zip' : 'Zip/Postal Code'}
                            maxLength={vm.burialBeneficiaryModel?.beneCountry === 'United States' ? 5 : 10}
                            required={false}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>}
              <div className="flex space-x-4 mt-6">
                {
                  vm.burialBeneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg ?
                    <Button title="Save & Continue" rounded onClick={async () => await handleSubmit(() => vm.createOrUpdateBurialBeneficiaryAsync())}
                      disabled={
                        vm.burialBeneficiaryModel?.lovBeneRelationshipId === undefined
                        // || vm.burialBeneficiaryModel?.burialPercentage === undefined
                        // || vm.burialBeneficiaryModel?.burialPercentage === 0
                        || vm.burialBeneficiaryModel?.beneOtherName === ""
                        || vm.burialBeneficiaryModel?.beneCountry === ""
                        || vm.burialBeneficiaryModel?.beneStreetAddress === ""
                        || vm.burialBeneficiaryModel?.beneCity === ""
                        || vm.burialBeneficiaryModel?.beneState === ""
                        || vm.burialBeneficiaryModel?.beneZip === ""
                      }
                    />
                    :
                    <Button title="Save & Continue" rounded onClick={async () => await handleSubmit(() => vm.createOrUpdateBurialBeneficiaryAsync())}
                      disabled={
                        vm.burialBeneficiaryModel?.lovBeneRelationshipId === undefined
                        || vm.burialBeneficiaryModel?.beneFirstName === ""
                        || vm.burialBeneficiaryModel?.beneLastName === ""
                        || vm.burialBeneficiaryModel?.beneBirthDate === ""
                        || vm.burialBeneficiaryModel?.beneSsn === ""
                        || vm.burialBeneficiaryModel?.beneCountry === ""
                        || vm.burialBeneficiaryModel?.beneStreetAddress === ""
                        || vm.burialBeneficiaryModel?.beneCity === ""
                        || vm.burialBeneficiaryModel?.beneState === ""
                        || vm.burialBeneficiaryModel?.beneZip === ""
                      }
                    />
                }
                <Button title="Cancel" severity='warning' outlined rounded onClick={async () => await vm.cancelAddBurialBeneficiaryAsync()} />
              </div>
            </div>
          ) : (<></>)
        }
        {
          vm.model.beneficiaryDesignation.burialBeneficiaries?.length! > 0 &&
          <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
            {!vm.beneficiaryBurialCurrentState?.summaryVisible
              && !(vm.beneficiaryBurialCurrentState!.isAddingBeneficiary || vm.beneficiaryBurialCurrentState!.isEditingBeneficiary) &&
              <>
                <Button
                  title="Save & Continue" rounded
                  onClick={async () => await handleSubmit(() => vm.onValidateBeneficiaryBurialStepCompleteAsync())}
                  disabled={vm.model.beneficiaryDesignation.burialBeneficiaries?.length === 0}
                />
                {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
                  onClick={async () => await vm.navigateToReviewStepAsync()}
                />}
              </>
            }
            {vm.beneficiaryBurialCurrentState!.summaryVisible &&
              <><Button
                title={`${vm.navigatedFromReviewScreen ? 'Save & Return to Review' : 'Save & Continue'}`}
                rounded
                onClick={async () => await handleSubmit(() => vm.onBeneficiaryBurialStepCompleteAsync())}
                disabled={!vm.model.beneficiaryDesignation.afrDecleration}
              />
                <Button
                  title="Edit Designation" rounded
                  severity='warning' outlined
                  onClick={() => vm.beneficiaryBurialCurrentState!.summaryVisible = false}
                />
              </>
            }

          </div>
        }
      </div >
    </>
  );
});

export default BeneficiaryDesignationBurialAllowance;
